/**
 * The Companion tab (Volunteers page): this week's items, each with its one press — Send reminders (an outing
 * tomorrow, tomorrow's visits), Call (a volunteer who has not confirmed by the morning), Confirm (a one-time
 * request someone took; answers to a regular request — nothing is assigned until this press), Resend the form
 * (completion forms pending), Review (an incident report). Below: the open requests and the outings.
 * 1.4.0 §2: a request with someone else coming waits for the Director's OK — the administrator gets Review
 * (Approve / Decline with a note); a co-administrator sees "Waiting for the Director's OK" and no button.
 */
import { useCallback, useEffect, useId, useRef, useState, type ReactNode } from 'react';
import { useQuery } from '@tanstack/react-query';
import { apiFetch } from '@/lib/api';
import { useTranslation } from 'react-i18next';
import type { CompanionItem, CompanionTabResponse, OutingView, RequestView, SendResult } from '@shared/volunteers-api';
import { directionsLinks, minutesLabel, ratioOk, tenantsFor } from '@shared/volunteers';
import type { TFunction } from 'i18next';
import { staffName } from '@shared/people';
import { useMe } from '@/auth/useMe';
import { PrintArea } from '@/lib/docs';
import { OutingSheet as OutingSheetPrint } from '@/features/print/Printables';
import { useAnnounce } from '@/components/Announcer';
import { useSimplerScreens } from '@/lib/simpler';
import { Icon } from '@/components/Icons';
import { CallLink } from '@/components/Contact';
import { Button } from '@/components/ui/button';
import { kindWords } from './kindWords';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Sheet, SheetContent } from '@/components/ui/sheet';
import { Link } from 'react-router-dom';
import { useToast } from '@/components/ui/use-toast';
import { formatDayKey } from '@/lib/dates';
import { errorText } from '@/features/people/api';
import { TextArea, TextField } from '@/features/people/fields';
import { postJson, usePicker, useVolunteerRefresh } from './api';
import { ReferralSheet, VisionsReviewSheet } from './Visions';
import { companionOnly, itemsForProgram, itemWords } from './companionItems';
import { VisitFiles, VisitFilesButton } from '@/features/files/VisitFiles';
import { OutingStandby183 } from './OutingStandby183';
import { ProposedTimes } from './ProposedTimes';
import { NoVolunteerButtons } from './NoVolunteerButtons';
import { EntrySheet } from './CompanionCloseout';
import { ReadingCloseoutSheet, type ReadingVisitRef } from './ReadingCloseoutSheet';

const DOT: Record<CompanionItem['kind'], string> = {
  newVolunteer: 'var(--st-attn-dot)',
  outingTomorrow: 'var(--st-attn-dot)',
  volunteerUnconfirmed: 'var(--st-hold-dot)',
  claimed: 'var(--st-conf-dot)',
  answers: 'var(--st-sched-dot)',
  completionsPending: 'var(--st-done-dot)',
  incidentReview: 'var(--st-attn-dot)',
  followUp: 'var(--st-hold-dot)',
  visionsReady: 'var(--st-sched-dot)',
  visionsReferral: 'var(--st-hold-dot)',
  needsOk: 'var(--st-attn-dot)',
  workshopProposal: 'var(--st-sched-dot)',
  activityHelp: 'var(--st-conf-dot)',
  noVolunteer: 'var(--st-attn-dot)',
  readingCloseout: 'var(--st-attn-dot)',
  activityCloseout: 'var(--st-attn-dot)',
  readingNote: 'var(--st-sched-dot)',
};

/** The Director's-OK item (moved to ./companionItems with every other item's words; kept here for older imports). */
export { needsOkWords } from './companionItems';

/** The second person on a request, in words: "with a guest, Rosa" / "with Ana (4B) · same apartment". */
/** 1.7.2: why "Send to everyone in Companion" is offered. */
function widenWhy(w: NonNullable<RequestView['widen']>, t: TFunction): string {
  if (w.reason === 'no-one-fits') return t('sched172.whyNoFit');
  return w.days === 0 ? t('sched172.whyNoAnswerNow') : t('sched172.whyNoAnswer', { count: w.days });
}

function secondWords(r: RequestView, t: TFunction): string {
  const s = r.second;
  if (!s) return '';
  if (s.kind === 'guest') return t('vol.req.withGuest', { name: s.firstName });
  return `${t('vol.req.withTenant', { name: s.firstName, unit: s.unit })}${s.sameApartment ? ` · ${t('vol.req.householdFirst')}` : ''}`;
}

/**
 * `openRequest` / `openIncident` (18 Oct 2026): an item on Today or a program's page opens its exact place here —
 * the request's sheet, or the incident report — once the tab's answer has the request; `onOpened` clears the address.
 */
export function CompanionTab({ data, loading, todayKey, openRequest, openIncident, onOpened, program, after, failed }: { data: CompanionTabResponse | undefined; loading: boolean; todayKey: string; openRequest?: string | null; openIncident?: string | null; onOpened?: () => void; program?: 'companion'; after?: ReactNode; /** the read failed and nothing is on screen: say so, never "nothing needs you" */ failed?: boolean }) {
  const { t, i18n } = useTranslation();
  const lang = i18n.language === 'es' ? 'es' : 'en';
  const announce = useAnnounce();
  const toast = useToast();
  const refresh = useVolunteerRefresh();
  const [busy, setBusy] = useState<string | null>(null);
  const [confirm, setConfirm] = useState<RequestView | null>(null);
  const [entry, setEntry] = useState<{ appointmentId: string; volunteerId: string; label: string; mode: 'completion' | 'incident' } | null>(null);
  const [activityClose, setActivityClose] = useState<ReadingVisitRef | null>(null);
  const [review, setReview] = useState<string | null>(null);
  const [visions, setVisions] = useState<string | null>(null);
  const [referral, setReferral] = useState<{ tenantId: string; label: string } | null>(null);
  const [deciding, setDeciding] = useState<RequestView | null>(null);
  const opener = useRef<HTMLElement | null>(null);
  // 19 Oct 2026 (D-1019-ATTN): no new-volunteer item here. 23 Oct 2026 (D-1023-READING): nor Reading's own close-out and notes
  // 25 Oct 2026 (D-1025-PROGRAM): on Companion's own page (program="companion") the list is Companion's alone, by the same rule as Reading's
  const items = program ? itemsForProgram(data?.items ?? [], data?.requests ?? [], 'companion') : companionOnly(data?.items ?? []).filter((i) => i.kind !== 'readingCloseout' && i.kind !== 'readingNote' && i.data.program !== 'READING');
  const requests = data?.requests ?? [];
  const needing = requests.filter((r) => r.state === 'needsOk' || !!r.widen);
  const director = useMe().data?.role === 'admin';
  useEffect(() => {
    if (openIncident) {
      setReview(openIncident);
      onOpened?.();
      return;
    }
    if (!openRequest) return;
    const r = data?.requests.find((x) => x.requestId === openRequest);
    if (!r) return;
    // the Director's OK opens its own review (the administrator); every other request opens its sheet
    if (r.state === 'needsOk' && director) setDeciding(r);
    else setConfirm(r);
    onOpened?.();
  }, [openRequest, openIncident, data?.requests, director, onOpened]);
  /** The administrator reviews; everyone else sees that it waits for the Director. */
  /** 1.7.2: "Send to everyone in Companion" — a person's press, offered with its reason; never automatic. */
  const widen = async (r: RequestView) => {
    setBusy(`widen:${r.requestId}`);
    try {
      const res = await postJson<{ send: SendResult }>(`/api/volunteers/requests/${r.requestId}`, { what: 'widen' });
      toast(res.send.message);
      announce(res.send.message);
      refresh();
    } catch (e) {
      const m = errorText(e);
      toast(m);
      announce(m, 'assertive');
    } finally {
      setBusy(null);
    }
  };
  const okAction = (r: RequestView | undefined, testId: string) =>
    !r ? null : director ? (
      <Button
        variant="secondary"
        size="sm"
        onClick={(e) => {
          opener.current = e.currentTarget;
          setDeciding(r);
        }}
        data-testid={`ci-approve-${testId}`}
      >
        {t('vol.ok.review')}
      </Button>
    ) : (
      <span className="status hold" data-testid={`ci-waiting-${testId}`}>
        {t('vol.ok.waiting')}
      </span>
    );

  const press = async (key: string, fn: () => Promise<SendResult | { messages?: string[]; message?: string }>) => {
    setBusy(key);
    try {
      const r = await fn();
      const m = 'messages' in r && r.messages ? r.messages.join(' ') : ((r as { message?: string }).message ?? t('vol.done.saved'));
      toast(m);
      announce(m);
      refresh();
    } catch (e) {
      const m = errorText(e);
      toast(m);
      announce(m, 'assertive');
    } finally {
      setBusy(null);
    }
  };

  /** The request an item is about, when it carries proposed times (a one-time schedule-first request). */
  const proposalsOf = (i: CompanionItem): RequestView | null => {
    const r = requests.find((x) => x.requestId === i.data.requestId);
    return r && (r.proposals ?? []).length ? r : null;
  };

  const itemAction = (i: CompanionItem) => {
    if (i.action === 'remind') {
      const eventId = i.data.eventId as string | undefined;
      return (
        <Button variant="secondary" size="sm" disabled={busy === i.id} onClick={() => press(i.id, () => (eventId ? postJson<SendResult>(`/api/volunteers/outings/${eventId}/remind`, {}) : postJson<SendResult>('/api/volunteers/reminders', { dayKey: i.data.dayKey })))} data-testid={`ci-remind-${i.id}`}>
          {t('vol.tab.sendReminders')}
        </Button>
      );
    }
    // 18 Oct 2026: no phone on file — the volunteer's card (their email, their visits) instead of no button
    if (i.action === 'call')
      return i.phone ? (
        <CallLink className="btn secondary sm" phone={i.phone}>
          <Icon name="phone" /> {t('vol.tab.call')}
        </CallLink>
      ) : (
        <Link className="btn secondary sm" to={`/volunteers?v=${encodeURIComponent(String(i.data.volunteerId))}`} data-testid={`ci-card-${i.id}`}>
          {t('vol.join.openCard')}
        </Link>
      );
    if (i.kind === 'noVolunteer')
      return (
        <NoVolunteerButtons
          request={requests.find((x) => x.requestId === i.data.requestId)}
          onOpen={(r, el) => {
            opener.current = el;
            setConfirm(r);
          }}
        />
      );
    if (i.action === 'confirm' || i.action === 'open') {
      const r = requests.find((x) => x.requestId === i.data.requestId);
      return r ? (
        <Button
          variant="secondary"
          size="sm"
          onClick={(e) => {
            opener.current = e.currentTarget;
            setConfirm(r);
          }}
          data-testid={`ci-confirm-${r.requestId}`}
        >
          {i.action === 'confirm' ? t('vol.tab.confirm') : t('vol.tab.findReplacement')}
        </Button>
      ) : null;
    }
    if (i.kind === 'activityCloseout')
      return (
        <Button
          variant="secondary"
          size="sm"
          onClick={() => setActivityClose({ appointmentId: String(i.data.appointmentId), who: String(i.data.activity), name: String(i.data.name).split(' ')[0] ?? '', day: formatDayKey(String(i.data.dayKey), i18n.language), duration: Number(i.data.duration ?? 60) })}
          data-testid={`activity-close-${String(i.data.appointmentId)}`}
        >
          {t('reading1023.closeOut')}
        </Button>
      );
    if (i.action === 'resend') {
      const ids = String(i.data.appointmentIds ?? '').split(',').filter(Boolean);
      return (
        <Button variant="secondary" size="sm" disabled={busy === i.id} onClick={() => press(i.id, async () => {
          let n = 0;
          for (const id of ids) n += (await postJson<SendResult>(`/api/volunteers/visits/${id}`, { what: 'send-form' }).catch(() => ({ queued: 0 }))).queued;
          return { message: t('vol.tab.formsResent', { count: n }) };
        })} data-testid="ci-resend">
          {t('vol.tab.resendForm')}
        </Button>
      );
    }
    if (i.action === 'visions')
      return (
        <Button variant="secondary" size="sm" onClick={() => setVisions(String(i.data.month))} data-testid={`ci-visions-${i.data.month}`}>
          {t('vol.visions.reviewSend')}
        </Button>
      );
    if (i.action === 'referral')
      return (
        <Button variant="secondary" size="sm" onClick={() => setReferral({ tenantId: String(i.data.tenantId), label: `${i.data.name} (${i.data.unit})` })} data-testid={`ci-referral-${i.data.tenantId}`}>
          {t('vol.visions.markMade')}
        </Button>
      );
    if (i.action === 'events')
      return (
        <Link className="btn secondary sm" to="/events#h-activities" data-testid={`ci-events-${i.id}`}>
          {t('activities.openEvents')}
        </Link>
      );
    if (i.action === 'volunteer')
      return (
        <Link className="btn secondary sm" to={`/volunteers?v=${encodeURIComponent(String(i.data.volunteerId))}`} data-testid={`ci-volunteer-${i.data.volunteerId}`}>
          {t('vol.join.openCard')}
        </Link>
      );
    if (i.action === 'approve') return okAction(requests.find((x) => x.requestId === i.data.requestId), String(i.data.requestId));
    // a follow-up opens the incident report it came from (18 Oct 2026: it had no button)
    if (i.action === 'review' || (i.kind === 'followUp' && i.data.incidentId))
      return (
        <Button variant="secondary" size="sm" onClick={() => setReview(String(i.data.incidentId))} data-testid={i.kind === 'followUp' ? `ci-followup-${i.data.incidentId}` : `ci-review-${i.data.incidentId}`}>
          {i.kind === 'followUp' ? t('compItem.openReport') : t('vol.tab.review')}
        </Button>
      );
    return null;
  };

  return (
    <>
      <Card as="section" aria-labelledby="h-comp-tab" data-testid="companion-tab">
        <CardHeader>
          <CardTitle id="h-comp-tab">{t('vol.tab.title')}</CardTitle>
          <span className="status attn" aria-label={t('vol.tab.count', { count: items.length })}>
            {items.length}
          </span>
        </CardHeader>
        <CardContent>
          {loading ? <p className="help">{t('app.loading')}</p> : null}
          {!loading && failed ? <p className="help">{t('vol.home.error')}</p> : null}
          {!loading && !failed && !items.length ? <p className="help calm">{t('vol.tab.none')}</p> : null}
          <ul className="attn-list">
            {items.map((i) => (
              <li key={i.id} data-testid={`ci-${i.kind}`}>
                <span className="dot" style={{ background: DOT[i.kind] }} />
                <div className="t">
                  <b>{itemWords(i, t, lang).text}</b>
                  {/* 22 Oct 2026: a one-time request with proposed times says them on one line, with Use this time */}
                  {(i.kind === 'answers' || i.kind === 'noVolunteer') && proposalsOf(i) ? null : <span>{itemWords(i, t, lang).sub}</span>}
                  {(i.kind === 'answers' || i.kind === 'noVolunteer') && proposalsOf(i) ? <ProposedTimes r={proposalsOf(i)!} /> : null}
                </div>
                {itemAction(i)}
              </li>
            ))}
          </ul>
        </CardContent>
      </Card>

      {after}

      {/* Eli, 17 Oct 2026: a request shows here only when it needs the office (the Director's OK, or
          nobody answered and it can go wider); the rest live on their program's page (Companion & Wellness, Reading & Services) */}
      {needing.length ? (
        <Card as="section" id="requests" aria-labelledby="h-requests" style={{ marginTop: 18 }} data-testid="requests-card">
          <CardHeader>
            <CardTitle id="h-requests">{t('vol.tab.requests')}</CardTitle>
          </CardHeader>
          <CardContent>
            <ul className="attn-list">
              {needing.map((r) => (
                <li key={r.requestId} data-testid={`req-${r.requestId}`}>
                  <span className="dot" style={{ background: r.state === 'confirmed' ? 'var(--st-conf-dot)' : r.state === 'claimed' || r.state === 'answered' ? 'var(--st-sched-dot)' : r.state === 'needsOk' ? 'var(--st-attn-dot)' : 'var(--st-hold-dot)' }} />
                  <div className="t">
                    <b>
                      {r.tenant.unit} · {r.tenant.firstName} · {kindWords(t, r)}
                      {r.code ? ` · ${r.code}` : ''} · {formatDayKey(r.dayKey, lang)} {minutesLabel(r.minutes)}
                    </b>
                    <span>
                      {t(`vol.req.state.${r.state}`, { name: r.claimedBy?.name ?? r.confirmed?.name ?? '', count: r.sentTo })}
                      {r.mode === 'consistent' ? ` · ${t('vol.req.everyWeek', { weeks: r.weeks })}` : ''}
                      {r.second ? ` · ${secondWords(r, t)}` : ''}
                    </span>
                    {r.widen ? (
                      <span data-testid={`widen-why-${r.requestId}`}>
                        {widenWhy(r.widen, t)} {t('sched172.widenHelp')}
                      </span>
                    ) : null}
                  </div>
                  {/* 1.8.3 (D-1.8.3-3): a request's buttons in one row, one order — Open (or the Director's OK) first, then Send to everyone */}
                  <div className="book183-row-actions" data-testid={`req-actions-${r.requestId}`}>
                    {r.state === 'needsOk' ? (
                      okAction(r, `req-${r.requestId}`)
                    ) : r.state !== 'past' && r.state !== 'cancelled' && r.state !== 'declined' ? (
                      <Button
                        variant="secondary"
                        size="sm"
                        onClick={(e) => {
                          opener.current = e.currentTarget;
                          setConfirm(r);
                        }}
                      >
                        {t('vol.open')}
                      </Button>
                    ) : null}
                    {r.widen ? (
                      <Button size="sm" disabled={busy !== null} aria-label={t('sched172.widenAria', { unit: r.tenant.unit, day: formatDayKey(r.dayKey, lang) })} onClick={() => widen(r)} data-testid={`widen-${r.requestId}`}>
                        <Icon name="mail" /> {t('sched172.widen')}
                      </Button>
                    ) : null}
                  </div>
                </li>
              ))}
            </ul>
          </CardContent>
        </Card>
      ) : null}

      {data?.outings.length ? <OutingsCard outings={data.outings} lang={lang} officePhone={data.officePhone} /> : null}

      <VisitsNeedingForms data={data} onEnter={(e) => setEntry(e)} />

      <Sheet open={!!confirm} onOpenChange={(o) => (!o ? setConfirm(null) : undefined)}>
        {confirm ? <ConfirmSheet r={confirm} onClose={() => setConfirm(null)} returnFocusTo={opener} todayKey={todayKey} /> : null}
      </Sheet>
      <Sheet open={!!activityClose} onOpenChange={(o) => (!o ? setActivityClose(null) : undefined)}>
        {activityClose ? <ReadingCloseoutSheet visit={activityClose} activity onClose={() => setActivityClose(null)} /> : null}
      </Sheet>
      <Sheet open={!!entry} onOpenChange={(o) => (!o ? setEntry(null) : undefined)}>
        {entry ? <EntrySheet entry={entry} onClose={() => setEntry(null)} /> : null}
      </Sheet>
      <Sheet open={!!visions} onOpenChange={(o) => (!o ? setVisions(null) : undefined)}>
        {visions ? <VisionsReviewSheet month={visions} onClose={() => setVisions(null)} /> : null}
      </Sheet>
      <Sheet open={!!referral} onOpenChange={(o) => (!o ? setReferral(null) : undefined)}>
        {referral ? <ReferralSheet tenantId={referral.tenantId} label={referral.label} onClose={() => setReferral(null)} /> : null}
      </Sheet>
      <Sheet open={!!review} onOpenChange={(o) => (!o ? setReview(null) : undefined)}>
        {review ? <ReviewSheet incidentId={review} onClose={() => setReview(null)} /> : null}
      </Sheet>
      <Sheet open={!!deciding} onOpenChange={(o) => (!o ? setDeciding(null) : undefined)}>
        {deciding ? <DirectorOkSheet r={deciding} onClose={() => setDeciding(null)} returnFocusTo={opener} /> : null}
      </Sheet>
    </>
  );
}

function OutingsCard({ outings, lang, officePhone }: { outings: OutingView[]; lang: 'en' | 'es'; officePhone: string | null }) {
  const { t } = useTranslation();
  const announce = useAnnounce();
  const toast = useToast();
  const refresh = useVolunteerRefresh();
  // 25 Oct 2026 (D-1025-PROGRAM): a saved outing waits for a person's Send; the press goes through the Outbox cancel window
  const [sending, setSending] = useState<string | null>(null);
  const [sentNow, setSentNow] = useState<Record<string, string>>({});
  const send = async (o: OutingView) => {
    setSending(o.eventId);
    try {
      const r = await postJson<SendResult>(`/api/volunteers/outings/${o.eventId}/send`, {});
      const m = r.message;
      if (r.queued > 0) setSentNow((x) => ({ ...x, [o.eventId]: m }));
      toast(m);
      announce(m);
      refresh();
    } catch (e) {
      const m = errorText(e);
      toast(m);
      announce(m, 'assertive');
    } finally {
      setSending(null);
    }
  };
  const [printing, setPrinting] = useState<OutingView | null>(null);
  const donePrinting = useCallback(() => setPrinting(null), []);
  return (
    <Card as="section" id="outings" aria-labelledby="h-outings" style={{ marginTop: 18 }} data-testid="outings-card">
      <CardHeader>
        <CardTitle id="h-outings">{t('vol.tab.outings')}</CardTitle>
      </CardHeader>
      <CardContent>
        <ul className="attn-list">
          {outings.map((o) => {
            const inN = o.interested.filter((i) => !i.standby).length;
            const standby = o.interested.length - inN;
            const over = !ratioOk(inN, o.tenantsComing);
            return (
              <li key={o.eventId}>
                <span className="dot" style={{ background: 'var(--st-sched-dot)' }} />
                <div className="t">
                  <b>
                    {o.name} · {formatDayKey(o.dayKey, lang)} {minutesLabel(o.minutes)}
                  </b>
                  <span>
                    {o.volunteersNeeded ? t('vol.outing.counter', { n: inN, of: o.volunteersNeeded }) : t('vol.outing.counterNoCap', { n: inN })}
                    {standby ? ` · ${t('vol.outing.standby', { count: standby })}` : ''} · {t('vol.outing.ratioLine', { v: inN, max: tenantsFor(inN) })} · {t('vol.outing.tenants', { count: o.tenantsComing, cap: o.tenantCapacity })}
                  </span>
                  {over && o.tenantsComing ? (
                    <span data-testid={`outing-ratio-warn-${o.eventId}`}>
                      <Icon name="alert" width={14} height={14} /> {t('vol.outing.ratioOverShort')}
                    </span>
                  ) : null}
                  <span data-testid={`outing-sent-${o.eventId}`}>
                    {o.sent === false ? t('prog1025.outing.notSent') : t('prog1025.outing.sentTo', { count: o.sentTo })}
                  </span>
                  <OutingStandby183 outing={o} lang={lang} />
                </div>
                {o.sent === false ? (
                  <Button size="sm" busy={sending === o.eventId} busyLabel={t('prog1025.outing.sending')} disabled={sending !== null} aria-label={t('prog1025.outing.sendAria', { name: o.name })} onClick={() => void send(o)} data-testid={`send-outing-${o.eventId}`}>
                    <Icon name="mail" /> {t('prog1025.outing.send')}
                  </Button>
                ) : sentNow[o.eventId] ? (
                  <span className="status conf" role="status">{t('prog1025.outing.sentDone')}</span>
                ) : null}
                <Button variant="secondary" size="sm" onClick={() => setPrinting(o)} aria-label={t('vol.outing.printName', { name: o.name })} data-testid={`print-outing-${o.eventId}`}>
                  <Icon name="printer" /> {t('vol.outing.print')}
                </Button>
                <VisitFilesButton kind="outing" id={o.eventId} label={t('files.forOuting', { name: o.name })} />
              </li>
            );
          })}
        </ul>
        <PrintArea active={!!printing} onDone={donePrinting}>
          {printing ? (
            <OutingSheetPrint
              o={{
                title: printing.name,
                dayLabel: formatDayKey(printing.dayKey, lang),
                meetTime: minutesLabel(printing.minutes),
                meetPlace: t('vol.outing.lobby'),
                placeName: printing.place.name,
                address: printing.place.address,
                directions: [],
                directionsUrl: printing.place.address ? directionsLinks(printing.place.address).google : '',
                volunteers: printing.interested.filter((i) => !i.standby).map((i) => i.name),
                officePhone: officePhone ?? '—',
              }}
            />
          ) : null}
        </PrintArea>
      </CardContent>
    </Card>
  );
}

/** Visits that ended without the five questions: staff can enter the form for a volunteer who stops by. */
function VisitsNeedingForms({ data, onEnter }: { data: CompanionTabResponse | undefined; onEnter: (e: { appointmentId: string; volunteerId: string; label: string; mode: 'completion' | 'incident' }) => void }) {
  const { t, i18n } = useTranslation();
  const pending = data?.items.find((i) => i.kind === 'completionsPending');
  const ids = String(pending?.data.appointmentIds ?? '').split(',').filter(Boolean);
  const rows = ids
    .map((id) => {
      const r = data?.requests.find((x) => x.confirmed?.appointmentIds.includes(id));
      return r ? { id, r } : null;
    })
    .filter((x): x is { id: string; r: RequestView } => !!x);
  if (!rows.length) return null;
  return (
    <Card as="section" aria-labelledby="h-pending" style={{ marginTop: 18 }} data-testid="pending-forms">
      <CardHeader>
        <CardTitle id="h-pending">{t('vol.tab.pendingTitle')}</CardTitle>
      </CardHeader>
      <CardContent>
        <ul className="attn-list">
          {rows.map(({ id, r }) => (
            <li key={id}>
              <span className="dot" style={{ background: 'var(--st-hold-dot)' }} />
              <div className="t">
                <b>
                  {r.confirmed?.name} · {kindWords(t, r)} · {r.tenant.unit}
                </b>
                <span>{formatDayKey(r.dayKey, i18n.language)}</span>
              </div>
              <Button variant="secondary" size="sm" onClick={() => onEnter({ appointmentId: id, volunteerId: r.confirmed!.volunteerId, label: `${r.confirmed?.name} · ${r.tenant.unit}`, mode: 'completion' })} data-testid={`enter-${id}`}>
                {t('vol.tab.enterForm')}
              </Button>
            </li>
          ))}
        </ul>
      </CardContent>
    </Card>
  );
}

/**
 * 3 Nov 2026 (D-1103-SIMPLE-4): the ways to send a request out again (to those free then, to everyone, widen) are one
 * "Send it out again" under Simpler screens; with the switch off they are the row of buttons they were.
 */
function SendAgain({ simpler, children }: { simpler: boolean; children: ReactNode }) {
  const { t } = useTranslation();
  if (!simpler) return <div className="chips">{children}</div>;
  return (
    <details data-testid="confirm-send-again">
      <summary>{t('simp1103.confirm.again')}</summary>
      <div className="chips" style={{ paddingTop: 8 }}>
        {children}
      </div>
    </details>
  );
}

export function ConfirmSheet({ r, onClose, returnFocusTo, todayKey }: { r: RequestView; onClose: () => void; returnFocusTo: React.RefObject<HTMLElement | null>; todayKey: string }) {
  const { t, i18n } = useTranslation();
  const lang = i18n.language === 'es' ? 'es' : 'en';
  const announce = useAnnounce();
  const toast = useToast();
  const refresh = useVolunteerRefresh();
  const simpler = useSimplerScreens();
  const uid = useId();
  const candidates = r.mode === 'one-time' && r.claimedBy ? [{ volunteerId: r.claimedBy.volunteerId, name: r.claimedBy.name, answer: 'take' as const, proposed: null }] : r.mode === 'one-time' && r.audienceRule !== 'schedule-first' ? [] : r.answers.filter((a) => a.answer !== 'no');
  const scheduleFirst = r.audienceRule === 'schedule-first';
  const [pick, setPick] = useState(candidates[0]?.volunteerId ?? '');
  const [notice, setNotice] = useState(true);
  const [wheel, setWheel] = useState(false);
  const [useProposed, setUseProposed] = useState(false);
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState<string | null>(null);
  const [reason, setReason] = useState('');
  // 18 Oct 2026: who asked to cancel — the office, or the tenant (who called or came by); it was always "the office"
  const [cancelWho, setCancelWho] = useState<'Staff' | 'Tenant'>('Staff');
  const [cantWhy, setCantWhy] = useState('');
  // Reading & Services says its own name (18 Oct 2026: its resend press said "Companion")
  const programName = r.program === 'READING' ? t('vol.program.reading') : t('vol.program.companion');
  // Eli, 17 Oct 2026: a matched request opens here too — the volunteer called or emailed to cancel
  const matched = r.state === 'confirmed' && !!r.confirmed;
  const chosen = candidates.find((c) => c.volunteerId === pick);
  // Eli, 15 Oct 2026: a volunteer who said yes by email or phone — the office picks them here; Confirm is still the press
  const ready = usePicker({ category: r.category, dayKey: r.dayKey, minutes: r.minutes });
  const freeThen = new Set((ready.data?.available ?? []).map((v) => v.volunteerId));
  const takenByOther = r.mode === 'one-time' && !!r.claimedBy;

  /** `mine`: the press's own words in the reader's language win over the API's English message. */
  const go = async (body: Record<string, unknown>, done: string, mine = false) => {
    setBusy(true);
    try {
      const res = await postJson<{ messages?: string[] }>(`/api/volunteers/requests/${r.requestId}`, body);
      const m = mine ? done : (res.messages?.join(' ') ?? done);
      setMsg(m);
      toast(m);
      announce(m);
      refresh();
      if (body.what !== 'send' && body.what !== 'widen') onClose();
    } catch (e) {
      const m = errorText(e);
      setMsg(m);
      announce(m, 'assertive');
    } finally {
      setBusy(false);
    }
  };

  return (
    <SheetContent
      title={t('vol.confirm.title', { unit: r.tenant.unit, activity: kindWords(t, r) })}
      returnFocusTo={returnFocusTo}
      footer={
        <>
          <Button variant="secondary" onClick={onClose}>
            {t('vol.cancel')}
          </Button>
          {matched ? null : (
            <Button
              disabled={busy || !pick}
              onClick={() => go({ what: 'confirm', volunteerId: pick, notice, wheelchairTraining: wheel, ...(useProposed && chosen?.proposed ? chosen.proposed : {}) }, t('vol.confirm.done'))}
              data-testid="confirm-match"
            >
              {t('vol.confirm.press')}
            </Button>
          )}
        </>
      }
    >
      <p>
        {r.tenant.firstName} · {r.tenant.unit} · {formatDayKey(r.dayKey, lang)} {minutesLabel(r.minutes)} · {r.mode === 'consistent' ? t('vol.req.everyWeek', { weeks: r.weeks }) : t('vol.req.oneTime')}
        {r.destination ? ` · ${[r.destination, ...(r.moreStops ?? [])].map((x) => x.name).join(' → ')}` : ''}
      </p>
      {r.tenant.phone ? (
        <p>
          <CallLink className="btn secondary sm" phone={r.tenant.phone} label={t('vol.confirm.callTenant', { name: r.tenant.firstName })}>
            <Icon name="phone" /> {t('vol.confirm.callTenant', { name: r.tenant.firstName })}
          </CallLink>
        </p>
      ) : null}
      {matched ? null : <ProposedTimes r={r} />}
      {r.lastDrop ? (
        <p className="status hold" data-testid="confirm-dropped">
          {r.lastDrop.via === 'office' ? t('compItem.dropOffice', { name: r.lastDrop.name, reason: r.lastDrop.reason }) : t('compItem.dropLink', { name: r.lastDrop.name })}
        </p>
      ) : null}
      {matched ? (
        <div data-testid="confirm-matched">
          <p>
            <b>{t('vb.c.matched', { name: r.confirmed!.name })}</b>
          </p>
          {r.mode === 'one-time' ? (
            <details>
              <summary data-testid="confirm-cant-open">{t('vb.c.cantMake', { name: r.confirmed!.name })}</summary>
              <div className="row-form">
                <TextField id={`${uid}-cant`} label={t('vb.c.cantHow')} value={cantWhy} onChange={setCantWhy} />
                <Button size="sm" variant="secondary" disabled={busy || !cantWhy.trim()} onClick={() => go({ what: 'volunteerCancelled', reason: cantWhy.trim() }, t('vb.c.cantDone'))} data-testid="confirm-cant">
                  {t('vb.c.cantPress')}
                </Button>
              </div>
              <p className="help">{t('vb.c.cantHelp')}</p>
            </details>
          ) : (
            <p className="help">{t('vb.c.weeklyHelp')}</p>
          )}
        </div>
      ) : null}
      {matched ? null : candidates.length ? (
        <fieldset className="field">
          <legend>{r.mode === 'one-time' && r.claimedBy ? t('vol.confirm.tookIt') : t('vol.confirm.answers')}</legend>
          <div className="radio-list">
            {candidates.map((c) => (
              <label key={c.volunteerId} htmlFor={`${uid}-${c.volunteerId}`}>
                <input id={`${uid}-${c.volunteerId}`} type="radio" name={`${uid}-v`} checked={pick === c.volunteerId} onChange={() => setPick(c.volunteerId)} />
                <span>
                  {c.name}
                  {'proposed' in c && c.proposed ? ` · ${t('vol.confirm.proposes', { when: `${formatDayKey(c.proposed.dayKey, lang)} ${minutesLabel(c.proposed.minutes)}` })}` : ''}
                </span>
              </label>
            ))}
          </div>
        </fieldset>
      ) : (
        <p className="help" data-testid="confirm-none">
          {t('vol.confirm.noneYet', { count: r.sentTo })}
        </p>
      )}
      {!takenByOther && !matched ? (
        <div className="field">
          <label htmlFor={`${uid}-any`}>{t('vol.confirm.pickAny')}</label>
          <select id={`${uid}-any`} className="input" value={candidates.some((c) => c.volunteerId === pick) ? '' : pick} onChange={(e) => setPick(e.target.value || candidates[0]?.volunteerId || '')} aria-describedby={`${uid}-any-help`} data-testid="confirm-pick-any">
            <option value="">{t('vol.confirm.pickAnyNone')}</option>
            {(ready.data?.all ?? []).map((v) => (
              <option key={v.volunteerId} value={v.volunteerId}>
                {v.name}
                {freeThen.has(v.volunteerId) ? ` · ${t('vol.confirm.freeThen')}` : ''}
              </option>
            ))}
          </select>
          <p className="help" id={`${uid}-any-help`}>
            {t('vol.confirm.pickAnyHelp')}
          </p>
        </div>
      ) : null}
      {chosen && 'proposed' in chosen && chosen.proposed ? (
        <label className="switch" htmlFor={`${uid}-prop`}>
          <input id={`${uid}-prop`} type="checkbox" checked={useProposed} onChange={(e) => setUseProposed(e.target.checked)} />
          <span>{t('vol.confirm.useProposed')}</span>
        </label>
      ) : null}
      {matched ? null : (
        <>
      <label className="switch" htmlFor={`${uid}-notice`}>
        <input id={`${uid}-notice`} type="checkbox" checked={notice} onChange={(e) => setNotice(e.target.checked)} />
        <span>{t('vol.confirm.notice')}</span>
      </label>
      <label className="switch" htmlFor={`${uid}-wheel`}>
        <input id={`${uid}-wheel`} type="checkbox" checked={wheel} onChange={(e) => setWheel(e.target.checked)} />
        <span>{t('vol.confirm.wheelchair')}</span>
      </label>
      <p className="help">{t('vol.confirm.help')}</p>
        </>
      )}
      {matched ? null : scheduleFirst ? (
        <>
          <p className="help" data-testid="confirm-schedule-first">
            {t('sched172.scheduleFirstNote')}
            {r.widen ? ` ${widenWhy(r.widen, t)} ${t('sched172.widenHelp')}` : ''}
          </p>
          <SendAgain simpler={simpler}>
            <Button variant="secondary" size="sm" disabled={busy || r.dayKey < todayKey} onClick={() => go({ what: 'send', audience: 'available' }, t('vol.req.sent'))}>
              {t('sched172.resendFit')}
            </Button>
            <Button variant="secondary" size="sm" disabled={busy || r.dayKey < todayKey} onClick={() => go({ what: 'send', audience: 'all' }, t('vol.req.sent'))} data-testid="confirm-resend-all">
              {t('vb.c.resendAll', { program: programName })}
            </Button>
            {r.widen ? (
              <Button variant="secondary" size="sm" disabled={busy} onClick={() => go({ what: 'widen' }, t('vol.req.sent'))} data-testid="confirm-widen">
                {t('sched172.widen')}
              </Button>
            ) : null}
          </SendAgain>
        </>
      ) : (
        <SendAgain simpler={simpler}>
          <Button variant="secondary" size="sm" disabled={busy || r.dayKey < todayKey} onClick={() => go({ what: 'send', audience: 'available' }, t('vol.req.sent'))}>
            {t('vol.confirm.resendAvailable')}
          </Button>
          <Button variant="secondary" size="sm" disabled={busy || r.dayKey < todayKey} onClick={() => go({ what: 'send', audience: 'all' }, t('vol.req.sent'))} data-testid="confirm-resend-all">
            {t('vb.c.resendAll', { program: programName })}
          </Button>
        </SendAgain>
      )}
      <details>
        <summary>{t('vol.confirm.cancelRequest')}</summary>
        <fieldset className="field" data-testid="cancel-who">
          <legend>{t('compItem.cancelWho')}</legend>
          <div className="radio-list">
            {(['Staff', 'Tenant'] as const).map((w) => (
              <label key={w} className="radio" htmlFor={`${uid}-who-${w}`}>
                <input id={`${uid}-who-${w}`} type="radio" name={`${uid}-who`} value={w} checked={cancelWho === w} onChange={() => setCancelWho(w)} data-testid={`cancel-who-${w}`} />
                <span>{w === 'Tenant' ? t('compItem.cancelWhoTenant') : t('compItem.cancelWhoOffice')}</span>
              </label>
            ))}
          </div>
        </fieldset>
        <div className="row-form">
          <TextField id={`${uid}-why`} label={t('vol.confirm.why')} value={reason} onChange={setReason} />
          <Button size="sm" variant="secondary" disabled={busy || !reason.trim()} onClick={() => go({ what: 'cancel', reason, initiatedBy: cancelWho }, cancelWho === 'Tenant' ? t('compItem.cancelledTenant') : t('compItem.cancelledOffice'), true)} data-testid="confirm-cancel-it">
            {t('vol.confirm.cancelIt')}
          </Button>
        </div>
      </details>
      {msg ? <p role="status">{msg}</p> : null}
    </SheetContent>
  );
}

/**
 * The Director's OK (1.4.0 §2): who is coming, the note (required), Approve — the request goes out exactly as a
 * normal one — or Decline — it is cancelled; nothing was sent. The server refuses anyone but the administrator.
 */
function DirectorOkSheet({ r, onClose, returnFocusTo }: { r: RequestView; onClose: () => void; returnFocusTo: React.RefObject<HTMLElement | null> }) {
  const { t, i18n } = useTranslation();
  const lang = i18n.language === 'es' ? 'es' : 'en';
  const announce = useAnnounce();
  const toast = useToast();
  const refresh = useVolunteerRefresh();
  const uid = useId();
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const decide = async (what: 'approve' | 'decline') => {
    setErr(null);
    if (!note.trim()) {
      setErr(t('vol.ok.needNote'));
      announce(t('vol.ok.needNote'), 'assertive');
      return;
    }
    setBusy(true);
    try {
      const res = await postJson<{ messages?: string[] }>(`/api/volunteers/requests/${r.requestId}`, { what, note: note.trim() });
      const m = res.messages?.join(' ') ?? t('vol.done.saved');
      toast(m);
      announce(m);
      refresh();
      onClose();
    } catch (e) {
      const m = errorText(e);
      setErr(m);
      announce(m, 'assertive');
    } finally {
      setBusy(false);
    }
  };
  return (
    <SheetContent
      title={t('vol.ok.title')}
      returnFocusTo={returnFocusTo}
      footer={
        <>
          <Button variant="secondary" disabled={busy} onClick={() => decide('decline')} data-testid="ok-decline">
            {t('vol.ok.decline')}
          </Button>
          <Button disabled={busy} onClick={() => decide('approve')} data-testid="ok-approve">
            {t('vol.ok.approve')}
          </Button>
        </>
      }
    >
      <dl className="kv" data-testid="ok-summary">
        <dt>{kindWords(t, r)}</dt>
        <dd>
          {r.tenant.unit} · {r.tenant.firstName} · {formatDayKey(r.dayKey, lang)} {minutesLabel(r.minutes)}
          {r.destination ? ` · ${[r.destination, ...(r.moreStops ?? [])].map((x) => x.name).join(' → ')}` : ''}
        </dd>
        <dt>{t('vol.ok.who')}</dt>
        <dd>
          {r.tenant.firstName}
          {r.second ? `, ${secondWords(r, t)}` : ''}, {t('vol.ok.andMore')}
        </dd>
        {r.approval ? (
          <>
            <dt>{t('vol.ok.asked')}</dt>
            <dd>{t('vol.ok.askedBy', { who: staffName(r.approval.askedBy), when: formatDayKey(r.approval.askedAt.slice(0, 10).replace(/-/g, ''), lang) })}</dd>
          </>
        ) : null}
        {r.notes ? (
          <>
            <dt>{t('vol.req.notes')}</dt>
            <dd>{r.notes}</dd>
          </>
        ) : null}
      </dl>
      <TextArea id={`${uid}-note`} label={t('vol.ok.note')} required value={note} onChange={setNote} help={t('vol.ok.noteHelp')} error={err && !note.trim() ? 'required' : undefined} />
      <p className="help">{t('vol.ok.help')}</p>
      {err ? (
        <p className="field-err" role="alert" data-testid="ok-problem">
          <Icon name="alert" width={16} height={16} /> {err}
        </p>
      ) : null}
    </SheetContent>
  );
}

function ReviewSheet({ incidentId, onClose }: { incidentId: string; onClose: () => void }) {
  const { t } = useTranslation();
  const announce = useAnnounce();
  const refresh = useVolunteerRefresh();
  const uid = useId();
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState(false);
  const q = useQuery({ queryKey: ['incident', incidentId], queryFn: () => apiFetch<{ report: Record<string, unknown>; volunteerName: string; tenantName: string; unit: string; dayKey: string; folder: string; photos: string[] }>(`/api/volunteers/incidents/${incidentId}`) });
  const report = q.data;
  const r = report?.report as Record<string, string> | undefined;
  return (
    <SheetContent
      title={t('vol.review.title')}
      wide
      footer={
        <>
          <Button variant="secondary" onClick={onClose}>
            {t('vol.cancel')}
          </Button>
          <Button
            disabled={busy}
            onClick={() => {
              setBusy(true);
              void postJson(`/api/volunteers/incidents/${incidentId}`, { note })
                .then(() => {
                  announce(t('vol.review.done'));
                  refresh();
                  onClose();
                })
                .catch((e) => announce(errorText(e), 'assertive'))
                .finally(() => setBusy(false));
            }}
            data-testid="incident-reviewed"
          >
            {t('vol.review.press')}
          </Button>
        </>
      }
    >
      <p className="eyebrow">{t('vol.inc.confidential')}</p>
      {report && r ? (
        <dl className="kv" data-testid="incident-review">
          <dt>{t('vol.review.who')}</dt>
          <dd>
            {report.volunteerName} · {report.tenantName} {report.unit}
          </dd>
          <dt>{t('vol.inc.whatHappened')}</dt>
          <dd lang="en">{r.whatHappened}</dd>
          <dt>{t('vol.inc.tenantOkayNow')}</dt>
          <dd>{r.tenantOkayNow === 'yes' ? t('vol.yes') : t('vol.no')}</dd>
          <dt>{t('vol.review.filed')}</dt>
          <dd>{report.folder}/</dd>
        </dl>
      ) : (
        <p className="help">{t('app.loading')}</p>
      )}
      <TextField id={`${uid}-note`} label={t('vol.review.note')} value={note} onChange={setNote} />
      <p className="help">{t('vol.review.help')}</p>
      <section aria-labelledby={`${uid}-files`} className="field">
        <h3 id={`${uid}-files`} className="vf-h">
          {t('files.forReport')}
        </h3>
        <VisitFiles kind="companion-incident" id={incidentId} />
      </section>
    </SheetContent>
  );
}
